/**
 * HITL approval dialog (P4-15, MUI port): Approve, Reject, and
 * Reject & Overwrite with a corrected upload. The intermediate file is
 * downloadable as Excel right here — a reviewer who cannot open the file
 * cannot review it.
 *
 * The pipeline itself runs without a model; the agent is ON DEMAND here:
 * « Ask the agent » has the audit assistant review this step's document
 * (its warnings, controls, unmatched rows) and propose what to do, inside
 * the dialog — the decision stays the reviewer's.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { api, type AuditAnswer, type PendingApproval } from "../lib/api";
import { FilePicker } from "./ui/FilePicker";
import { Markdown } from "./ui/Markdown";
import { ProposedActions } from "./ProposedActions";
import { StateChip } from "./ui/StateChip";
import { errorText } from "../lib/errors";
import { aiEnabled, requireCustomerSuffix } from "../lib/states";
import { expectedName, uploadNameProblem } from "../lib/uploadNames";
import { useToast } from "../hooks/useToast";
import { useCurrentCustomer, useEditMode } from "../hooks/useCustomer";

type AgentReview =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; answer: AuditAnswer }
  | { status: "error"; message: string };

type Decision = "approve" | "reject" | "replace";

export function ApprovalDialog({
  item,
  onClose,
  onDecided,
}: {
  item: PendingApproval;
  onClose: () => void;
  onDecided: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  // greyed, not removed: the operator sees the feature exists and is
  // told why it does nothing, instead of meeting an error from a route
  // that was never going to answer.
  const ai = aiEnabled();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<Decision | null>(null);
  const [error, setError] = useState("");
  const [replacement, setReplacement] = useState<File | null>(null);
  const current = useCurrentCustomer();
  const edit = useEditMode();
  // the corrected file follows the same rule as any upload
  const chooseReplacement = (file: File) => {
    const problem = current && requireCustomerSuffix() ? uploadNameProblem(file.name, current) : null;
    if (problem && current) {
      setReplacement(null);
      setError(
        problem.kind === "format"
          ? t("upload.badFormat", { name: file.name, pattern: expectedName(current.code) })
          : t("upload.customerMismatch", {
              name: file.name, found: problem.found, customer: current.name, code: current.code,
            }),
      );
      return;
    }
    setError("");
    setReplacement(file);
  };
  const locked = busy !== null || !edit.canEdit;
  const [review, setReview] = useState<AgentReview>({ status: "idle" });

  async function askAgent() {
    setReview({ status: "loading" });
    try {
      const answer = await api.ask(
        t("approval.agentQuestion", { step: item.step_key }),
        item.job_id,
      );
      setReview({ status: "done", answer });
    } catch (err) {
      setReview({ status: "error", message: errorText(err) });
    }
  }

  async function decide(decision: Decision) {
    setError("");
    setBusy(decision);
    try {
      if (decision === "approve") {
        await api.approve(item.job_id, item.step_id, note);
      } else if (decision === "reject") {
        await api.reject(item.job_id, item.step_id, note);
      } else {
        if (!replacement) {
          setError(t("approval.chooseFile"));
          setBusy(null);
          return;
        }
        await api.replace(item.job_id, item.step_id, replacement, note);
      }
      toast(t("toasts.approvalDecided"), "success");
      onDecided();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={review.status === "done" ? "md" : "sm"}
      fullWidth
      aria-labelledby="approval-dialog-title"
    >
      <DialogTitle id="approval-dialog-title">
        {t("approval.title", { step: item.step_key })}
        <StateChip
          state="AWAITING_APPROVAL"
          fallbackLabel={item.dashboard_status}
          sx={{ ml: 1 }}
        />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {item.tool_name} · {t("files.rows", { count: item.rows_out })}
            {item.unmatched_rows > 0
              ? ` · ${t("files.unmatchedShort", { count: item.unmatched_rows })}`
              : ""}
          </Typography>
          {item.unmatched_rows > 0 && (
            <Alert severity="warning">
              {t("approval.unmatchedWarning", { count: item.unmatched_rows })}
            </Alert>
          )}
          {item.artifact_id && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => {
                // authenticated fetch — an href navigation has no bearer token
                void api
                  .downloadExcel(item.artifact_id!, item.display_name ?? undefined)
                  .catch((err: unknown) => toast(errorText(err), "error"));
              }}
            >
              {item.display_name ?? t("approval.output")} (.xlsx)
            </Button>
          )}
          <TextField
            label={t("approval.note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            // Initial focus lands in the note — never on a decision button,
            // where a stray Enter would approve/reject by accident.
            autoFocus
          />
          <div>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("approval.replace")}
            </Typography>
            <FilePicker onFile={chooseReplacement}>
              {(open) => (
                <Button
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={open}
                  sx={{ mt: 0.5 }}
                >
                  {replacement
                    ? t("approval.chosenFile", { name: replacement.name })
                    : t("approval.chooseFile")}
                </Button>
              )}
            </FilePicker>
          </div>
          {review.status !== "idle" && (
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t("approval.agentHeading")}
              </Typography>
              {review.status === "loading" && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2">{t("approval.agentBusy")}</Typography>
                </Stack>
              )}
              {review.status === "error" && <Alert severity="error">{review.message}</Alert>}
              {review.status === "done" && (
                <>
                  <Markdown text={review.answer.answer} />
                  {(review.answer.proposed_actions?.length ?? 0) > 0 && (
                    <ProposedActions actions={review.answer.proposed_actions ?? []} />
                  )}
                </>
              )}
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<SmartToyIcon />}
          disabled={busy !== null || review.status === "loading"}
          onClick={() =>
            ai ? void askAgent() : toast(t("common.aiDisabled"), "info")
          }
          sx={{
            mr: "auto",
            ...(ai
              ? {}
              : { color: "text.disabled", borderColor: "action.disabled" }),
          }}
        >
          {review.status === "done" ? t("approval.askAgentAgain") : t("approval.askAgent")}
        </Button>
        <Button
          color="success"
          variant="contained"
          disabled={locked}
          onClick={() => void decide("approve")}
        >
          {t("approval.approve")}
        </Button>
        <Button
          color="error"
          variant="outlined"
          disabled={locked}
          onClick={() => void decide("reject")}
        >
          {t("approval.reject")}
        </Button>
        <Button disabled={locked} onClick={() => void decide("replace")}>
          {t("approval.replace")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
